import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Tabs, Button, message, Space, Image, Typography, Tag, Select, Checkbox, Row, Col, Card, Tooltip } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import axios from 'axios';
import { API_URL } from '../../config';
import QuillEditor from '../common/QuillEditor';
import PushToEmModal from '../PushToEmModal';

const { Title: AntTitle } = Typography;

interface RoomEditProps {
  roomId: number;
  hotelId: number;
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
  emHotelId?: number;
}

const LANGUAGES = [
  { code: 'en-US', label: 'English (US)' },
  { code: 'en-GB', label: 'English (UK)' },
  { code: 'nl-NL', label: 'Dutch' },
  { code: 'de-DE', label: 'German' },
  { code: 'es-ES', label: 'Spanish' },
  { code: 'fr-FR', label: 'French' },
];

const RoomEdit: React.FC<RoomEditProps> = ({
  roomId,
  hotelId,
  visible,
  onClose,
  onSuccess,
  emHotelId,
}) => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [room, setRoom] = useState<any>(null);
  const [roomType, setRoomType] = useState<any>(null);
  const [roomFeatures, setRoomFeatures] = useState<any[]>([]);
  const [allHotelFeatures, setAllHotelFeatures] = useState<any[]>([]);
  const [assignedFeatureIds, setAssignedFeatureIds] = useState<string[]>([]);
  const [roomTypeImages, setRoomTypeImages] = useState<any[]>([]);
  const [allRoomTypes, setAllRoomTypes] = useState<any[]>([]);
  const [pushToEmVisible, setPushToEmVisible] = useState(false);
  const [emCategories, setEmCategories] = useState<any[]>([]);

  useEffect(() => {
    if (visible && roomId) {
      loadRoom();
      loadRoomTypes();
      if (emHotelId) {
        loadEmCategories();
      }
    }
  }, [visible, roomId, emHotelId]);

  const loadRoom = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/rooms/${roomId}`);
      const roomData = response.data;
      console.log('Room data loaded:', roomData);
      setRoom(roomData);

      // Set form values
      form.setFieldsValue({
        name: roomData.name,
        floorNumber: roomData.floorNumber,
        locationNotes: roomData.locationNotes,
        ...LANGUAGES.reduce((acc, lang) => {
          acc[`description_${lang.code}`] = roomData.descriptions?.[lang.code] || '';
          acc[`externalName_${lang.code}`] = roomData.externalNames?.[lang.code] || '';
          acc[`emName_${lang.code}`] = roomData.emNames?.[lang.code] || '';
          acc[`emDescription_${lang.code}`] = roomData.emDescriptions?.[lang.code] || '';
          return acc;
        }, {} as any),
      });

      // Load room type and its images
      console.log('roomData.roomTypeId:', roomData.roomTypeId);
      if (roomData.roomTypeId) {
        loadRoomType(roomData.roomTypeId);
      } else {
        console.warn('No roomTypeId found in room data');
      }

      // Load room features
      loadRoomFeatures(roomData.assignedFeatures || []);
    } catch (error) {
      message.error('Failed to load room');
    } finally {
      setLoading(false);
    }
  };

  const loadRoomTypes = async () => {
    try {
      const response = await axios.get(`${API_URL}/hotels/${hotelId}/room-types`);
      setAllRoomTypes(response.data);
    } catch (error) {
      console.error('Failed to load room types');
    }
  };

  const loadEmCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/em-hotels/${emHotelId}/categories`);
      setEmCategories(response.data);
    } catch (error) {
      console.error('Failed to load EM categories');
    }
  };

  const loadRoomType = async (roomTypeId: number) => {
    try {
      const response = await axios.get(`${API_URL}/room-types/${roomTypeId}`);
      console.log('Room Type loaded:', response.data);
      setRoomType(response.data);

      // Load images for this room type
      loadRoomTypeImages(response.data.mewsId);
    } catch (error) {
      console.error('Failed to load room type');
    }
  };

  const loadRoomTypeImages = async (mewsId: string) => {
    try {
      const response = await axios.get(`${API_URL}/hotels/${hotelId}/images`);
      const images = response.data.filter((img: any) => img.CategoryId === mewsId);
      setRoomTypeImages(images);
    } catch (error) {
      console.error('Failed to load images');
    }
  };

  const loadRoomFeatures = async (featureIds: string[]) => {
    try {
      const response = await axios.get(`${API_URL}/hotels/${hotelId}/room-features`);

      // Deduplicate features (the endpoint returns one row per language)
      const featureMap = new Map<string, any>();
      response.data.forEach((feat: any) => {
        if (!featureMap.has(feat.mewsId)) {
          featureMap.set(feat.mewsId, {
            mewsId: feat.mewsId,
            names: {},
            isActive: feat.isActive,
            classification: feat.classification,
          });
        }
        if (feat.language && feat.name) {
          featureMap.get(feat.mewsId).names[feat.language] = feat.name;
        }
      });

      const all = Array.from(featureMap.values()).filter((f) => f.isActive !== false);
      setAllHotelFeatures(all);
      setAssignedFeatureIds(featureIds);
      setRoomFeatures(all.filter((f) => featureIds.includes(f.mewsId)));
    } catch (error) {
      console.error('Failed to load room features');
    }
  };

  const toggleFeatureAssignment = (mewsId: string, checked: boolean) => {
    const next = checked
      ? Array.from(new Set([...assignedFeatureIds, mewsId]))
      : assignedFeatureIds.filter((id) => id !== mewsId);
    setAssignedFeatureIds(next);
    setRoomFeatures(allHotelFeatures.filter((f) => next.includes(f.mewsId)));
  };

  const handleCopyRoomTypeDescription = (langCode: string, checked: boolean) => {
    if (checked && roomType?.descriptions?.[langCode]) {
      form.setFieldsValue({
        [`description_${langCode}`]: roomType.descriptions[langCode],
      });
    } else if (!checked) {
      // Restore original room description when unchecked
      form.setFieldsValue({
        [`description_${langCode}`]: room?.descriptions?.[langCode] || '',
      });
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      // Build descriptions, externalNames, and EM fields objects
      const descriptions: Record<string, string> = {};
      const externalNames: Record<string, string> = {};
      const emNames: Record<string, string> = {};
      const emDescriptions: Record<string, string> = {};

      LANGUAGES.forEach((lang) => {
        if (values[`description_${lang.code}`]) {
          descriptions[lang.code] = values[`description_${lang.code}`];
        }
        if (values[`externalName_${lang.code}`]) {
          externalNames[lang.code] = values[`externalName_${lang.code}`];
        }
        if (values[`emName_${lang.code}`]) {
          emNames[lang.code] = values[`emName_${lang.code}`];
        }
        if (values[`emDescription_${lang.code}`]) {
          emDescriptions[lang.code] = values[`emDescription_${lang.code}`];
        }
      });

      await axios.put(`${API_URL}/rooms/${roomId}`, {
        name: values.name,
        floorNumber: values.floorNumber,
        locationNotes: values.locationNotes,
        descriptions,
        externalNames,
        emNames,
        emDescriptions,
        assignedFeatures: assignedFeatureIds,
      });

      message.success('Room updated successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to update room');
    } finally {
      setSaving(false);
    }
  };

  const handleCopyToEm = (langCode: string) => {
    const values = form.getFieldsValue();
    const externalName = values[`externalName_${langCode}`];
    const description = values[`description_${langCode}`];

    if (!externalName && !description) {
      message.warning('No original content to copy');
      return;
    }

    form.setFieldsValue({
      [`emName_${langCode}`]: externalName || '',
      [`emDescription_${langCode}`]: description || '',
    });

    message.success('Original content copied to EM fields');
  };

  const getCurrentRoomData = () => {
    const values = form.getFieldsValue();
    const emNames: Record<string, string> = {};
    const emDescriptions: Record<string, string> = {};

    LANGUAGES.forEach(lang => {
      if (values[`emName_${lang.code}`]) {
        emNames[lang.code] = values[`emName_${lang.code}`];
      }
      if (values[`emDescription_${lang.code}`]) {
        emDescriptions[lang.code] = values[`emDescription_${lang.code}`];
      }
    });

    return {
      ...room,
      emNames,
      emDescriptions,
    };
  };

  const languageTabs = LANGUAGES.map((lang) => ({
    key: lang.code,
    label: lang.label,
    children: (
      <>
        {/* Room Type Info for this language */}
        {roomType && roomType.names?.[lang.code] && (
          <div style={{ padding: 12, background: '#f5f5f5', borderRadius: 8, marginBottom: 12 }}>
            <AntTitle level={5} style={{ marginBottom: 8 }}>Room Type</AntTitle>
            <p style={{ marginBottom: 4 }}><strong>Name:</strong> {roomType.names[lang.code]}</p>
            {roomType.descriptions?.[lang.code] && (
              <div>
                <strong>Description:</strong>
                <div
                  style={{
                    marginTop: 4,
                    padding: 8,
                    background: '#fff',
                    borderRadius: 4,
                    border: '1px solid #d9d9d9',
                    maxHeight: 100,
                    overflow: 'auto'
                  }}
                  dangerouslySetInnerHTML={{ __html: roomType.descriptions[lang.code] }}
                />
                <Checkbox
                  style={{ marginTop: 8 }}
                  onChange={(e) => handleCopyRoomTypeDescription(lang.code, e.target.checked)}
                >
                  Use this description for the room
                </Checkbox>
              </div>
            )}
          </div>
        )}

        {/* Room Features for this language */}
        {(() => {
          const featuresForLang = roomFeatures.filter(f => {
            const hasName = f.names?.[lang.code];
            return hasName;
          });
          return featuresForLang.length > 0 && (
            <div style={{ padding: 12, background: '#f9f9f9', borderRadius: 8, marginBottom: 12 }}>
              <AntTitle level={5} style={{ marginBottom: 8 }}>Room Features</AntTitle>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
                gap: 8
              }}>
                {featuresForLang.map((feature: any, idx: number) => (
                  <div
                    key={idx}
                    style={{
                      padding: '6px 10px',
                      background: '#fff',
                      border: '1px solid #d9d9d9',
                      borderRadius: 4,
                      fontSize: 13,
                    }}
                  >
                    {feature.names[lang.code]}
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Side by Side Layout */}
        <Row gutter={24} align="top">
          {/* Left side: Original Content */}
          <Col span={11}>
            <Card
              title="Original Content (From PMS)"
              size="small"
              headStyle={{ background: '#fafafa', fontWeight: 600 }}
              bodyStyle={{ padding: 16 }}
            >
              <Form.Item
                label="External Name"
                name={`externalName_${lang.code}`}
              >
                <Input placeholder={`External name in ${lang.label}`} />
              </Form.Item>

              <Form.Item
                label="Description"
                name={`description_${lang.code}`}
              >
                <QuillEditor placeholder={`Description in ${lang.label}`} height={150} />
              </Form.Item>
            </Card>
          </Col>

          {/* Middle: Copy button */}
          <Col span={2} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 60 }}>
            <Tooltip title="Copy original content to EM fields">
              <Button
                type="primary"
                shape="circle"
                icon={<ArrowRightOutlined />}
                size="large"
                onClick={() => handleCopyToEm(lang.code)}
                style={{ boxShadow: '0 2px 8px rgba(24, 144, 255, 0.3)' }}
              />
            </Tooltip>
          </Col>

          {/* Right side: EM Content */}
          <Col span={11}>
            <Card
              title="EM Content"
              size="small"
              headStyle={{ background: '#f0f9ff', fontWeight: 600, color: '#1890ff' }}
              bodyStyle={{ padding: 16 }}
            >
              <Form.Item
                label="EM Name"
                name={`emName_${lang.code}`}
              >
                <Input placeholder={`EM name in ${lang.label}`} />
              </Form.Item>

              <Form.Item
                label="EM Description"
                name={`emDescription_${lang.code}`}
              >
                <QuillEditor placeholder={`EM description in ${lang.label}`} height={150} />
              </Form.Item>
            </Card>
          </Col>
        </Row>
      </>
    ),
  }));

  return (
    <>
      {contextHolder}
      <Modal
        title={`Edit Room: ${room?.name || ''}`}
        open={visible}
        onCancel={onClose}
        footer={[
          <Button key="cancel" onClick={onClose}>
            Cancel
          </Button>,
          <Button
            key="push-to-em"
            onClick={() => {
              // Check if already pushed
              if (room?.emRoomId && room?.emPmsRoomId) {
                messageApi.error('This room has already been pushed to EM');
                return;
              }

              // Check if EM content is properly filled (both name AND description required)
              const values = form.getFieldsValue();

              const hasCompleteEmContent = LANGUAGES.some(lang => {
                const hasName = values[`emName_${lang.code}`];
                const hasDescription = values[`emDescription_${lang.code}`];
                return hasName && hasDescription;
              });

              if (!hasCompleteEmContent) {
                messageApi.warning('Please fill in both EM Name and EM Description for at least one language before pushing to EM');
                return;
              }

              setPushToEmVisible(true);
            }}
            disabled={!emHotelId || (room?.emRoomId && room?.emPmsRoomId)}
          >
            {room?.emRoomId && room?.emPmsRoomId
              ? 'Already Pushed to EM'
              : !emHotelId
                ? 'Push to EM (No EM Hotel)'
                : 'Push to EM'}
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={saving}
            onClick={handleSave}
          >
            Save
          </Button>,
        ]}
        width={1200}
        destroyOnClose
        style={{ top: 20 }}
        styles={{
          body: { maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' },
        }}
      >
      <Form
        form={form}
        layout="vertical"
      >
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              label="Room Name/Number"
              name="name"
              rules={[{ required: true, message: 'Room name is required' }]}
            >
              <Input placeholder="e.g., 101, Suite A" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Floor Number"
              name="floorNumber"
            >
              <Input placeholder="e.g., 1, 2, Ground" />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              label="Location Notes"
              name="locationNotes"
            >
              <Input placeholder="Additional location information" />
            </Form.Item>
          </Col>
        </Row>

        {allHotelFeatures.length > 0 && (
          <div style={{ marginTop: 24, padding: 16, background: '#fafafa', borderRadius: 8, border: '1px solid #e8e8e8' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <AntTitle level={5} style={{ margin: 0 }}>
                Assigned Features ({assignedFeatureIds.length}/{allHotelFeatures.length})
              </AntTitle>
              <Space>
                <Button
                  size="small"
                  onClick={() => {
                    const all = allHotelFeatures.map((f) => f.mewsId);
                    setAssignedFeatureIds(all);
                    setRoomFeatures(allHotelFeatures);
                  }}
                >
                  Select All
                </Button>
                <Button
                  size="small"
                  onClick={() => {
                    setAssignedFeatureIds([]);
                    setRoomFeatures([]);
                  }}
                >
                  Clear
                </Button>
              </Space>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 8,
            }}>
              {allHotelFeatures.map((feature) => {
                const displayName =
                  feature.names?.['en-US'] ||
                  feature.names?.['en-GB'] ||
                  Object.values(feature.names || {})[0] ||
                  feature.mewsId;
                return (
                  <Checkbox
                    key={feature.mewsId}
                    checked={assignedFeatureIds.includes(feature.mewsId)}
                    onChange={(e) => toggleFeatureAssignment(feature.mewsId, e.target.checked)}
                  >
                    <span>{displayName as string}</span>
                    {feature.classification && (
                      <Tag style={{ marginLeft: 6 }} color="default">
                        {feature.classification}
                      </Tag>
                    )}
                  </Checkbox>
                );
              })}
            </div>
          </div>
        )}

        <Tabs items={languageTabs} style={{ marginTop: 24 }} />

        {roomTypeImages.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <AntTitle level={5}>Room Type Images ({roomTypeImages.length})</AntTitle>
            <Image.PreviewGroup>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 12 }}>
                {roomTypeImages.map((img) => (
                  <Image
                    key={img.ImageId}
                    src={img.Url}
                    alt={img.CategoryName}
                    width={150}
                    height={150}
                    style={{ objectFit: 'cover', borderRadius: 8, cursor: 'pointer' }}
                  />
                ))}
              </div>
            </Image.PreviewGroup>
          </div>
        )}
      </Form>
    </Modal>

    <PushToEmModal
      visible={pushToEmVisible}
      onClose={() => setPushToEmVisible(false)}
      entityType="room"
      entityId={roomId}
      entityData={getCurrentRoomData()}
      emHotelId={emHotelId}
      emCategories={emCategories}
      roomFeatures={roomFeatures}
      onSuccess={() => {
        message.success('Room pushed to EM successfully');
        setPushToEmVisible(false);
      }}
    />
  </>
  );
};

export default RoomEdit;
